import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { VoteHeader } from './VoteHeader'
import { ErrorBanner } from '../common/ErrorBanner'
import { InfoBanner } from '../common/InfoBanner'
import { LoadingPanel } from '../common/LoadingPanel'
import { VoteSubmitted } from './VoteSubmitted'
import { VoteBiography } from './VoteBiography'
import { SingleGroupVote } from './SingleGroupVote'
import { MultiGroupVote } from './MultiGroupVote'
import { AgmStatus } from '../agm/AgmStatus';
import { VoteInfo } from './VoteInfo';
import { Countdown } from '../common/Countdown';
import { ProxyNomination } from '../proxy/ProxyNomination';
import { useVoting } from '../../contexts/VotingContext';
import { VoterValidateResult } from '../../types/voteTypes';
import { Nominations } from './type/nominations/Nominations';

export const Vote = ({ receipt, personalId, previewId, bioId, validateState, isPreview, onUpdateAuth, isInfoPageUrl, setReceipt }) => {
    const [isLoading, setLoading] = useState(false)
    const [voteError, setVoteError] = useState(null)
    const [voteInfo, setVoteInfo] = useState(null)
    const [infoPageContent, setInfoPageContent] = useState(null)
    const [isInfoPageEnabled, setInfoPageEnabled] = useState(null)
    const [isJustSubmitted, setJustSubmitted] = useState(false)
    const [currentGroup, setCurrentGroup] = useState(null)
    const [currentGroupVote, setCurrentGroupVote] = useState(null)
    const [groupIds, setGroupIds] = useState(null)
    const [votingType, setVotingType] = useState(null)
    const [isShowVoting, setShowVoting] = useState(false)
    const [isShowInfoPage, setShowInfoPage] = useState(false)
    const [isShowProxy, setShowProxyPage] = useState(false)
    const [isShowBio, setShowBio] = useState(false)
    const [bioBallotOption, setBioBallotOption] = useState(null)
    const [agmGroupStatus, setAgmGroupStatus] = useState(null)
    const [isNotOpen, setNotOpen] = useState(false)
    const [isShowNominations, setShowNominations] = useState(false)
    const [nominationConfig, setNominationConfig] = useState(null)

    const { voter, ballot, proxyInfo, setProxyInfo, isAdmin } = useVoting();

    /**
     * Get group info
     *
     * @param {any} groupId Optional group ID if specific group required
     * @param {any} previewVotes Optional preview votes stored client side
     * @param {any} submitReceipt Optional receipt which can be supplied in cases like on submit where the prop from App.js hasnt update dyet
     * @param {any} onGetGroup Optional function to run on success
     */
    const getGroup = (groupId = null, previewVotes = null, submitReceipt = null, onGetGroup) => {
        axios.post('/vote/group', {
            id: voter.uniqueId,
            key: voter.guid,
            ballotId: ballot.id,
            previewId,
            receipt: submitReceipt ? submitReceipt : receipt,
            personalId,
            groupId
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    // Do we need to add any preview votes to the group options first?
                    let currentGroup = response.data.payload.group
                    if (previewVotes) {
                        for (let i = 0; i < previewVotes.length; i++) {
                            // Find option
                            let option = currentGroup.options.find(option => option.ballotOptionId === previewVotes[i].ballotOptionId)
                            if (option) {
                                option.score = previewVotes[i].score
                                option.textValue = previewVotes[i].textValue
                            }
                        }
                    }

                    setCurrentGroup(currentGroup)
                    setCurrentGroupVote(response.data.payload.groupVote)
                    setGroupIds(response.data.payload.groupIds)
                    setVotingType(response.data.payload.votingType)
                    setShowVoting(true)
                    setLoading(false)

                    if (onGetGroup) {
                        onGetGroup()
                    }
                }
                else {
                    setVoteError(response.data.errorMessage)
                }
            })
            .catch(function (error) {
                alert(error)
            })
    }

    const getInfoPage = () => {
        axios.post('/vote/info', {
            id: voter.uniqueId,
            key: voter.guid,
            receipt,
            personalId,
            ballotId: ballot.id,
            previewId,
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    setInfoPageEnabled(response.data.payload.isInfoPageEnabled)
                    setInfoPageContent(response.data.payload.infoPageContent)
                }
                else {
                    setVoteError(response.data.errorMessage)
                }
            })
            .catch(function (error) {
                alert(error)
            })
    }

    const getBallotProxy = () => {
        axios.post('/vote/proxy', {
            id: voter.uniqueId,
            key: voter.guid,
            personalId,
            ballotId: ballot.id,
            previewId,
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    setShowProxyPage(true)
                    setProxyInfo(response.data.payload)
                }
                else {
                    setVoteError(response.data.errorMessage)
                }

                setLoading(false)
            })
            .catch(function (error) {
                alert(error)
            })
    }

    const getNominationConfig = () => {
        axios.post('/vote/ballotNomination', {
            id: voter.uniqueId,
            key: voter.guid,
            personalId,
            ballotId: ballot.id,
            previewId,
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    setShowNominations(true)
                    setNominationConfig(response.data.payload)
                }
                else {
                    setVoteError(response.data.errorMessage)
                }

                setLoading(false)
            })
            .catch(function (error) {
                alert(error)
            })
    }

    const getAgmGroupStatus = (onComplete) => {
        axios.post('/vote/agmGroupStatus', {
            id: voter.uniqueId,
            key: voter.guid,
            receipt,
            personalId,
            ballotId: ballot.id,
            previewId,
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    setAgmGroupStatus(response.data.payload)

                    if (onComplete) {
                        onComplete()
                    }
                }
                else {
                    setVoteError(response.data.errorMessage)
                }
            })
            .catch(function (error) {
                alert(error)
            })
    }

    // AGM group status
    useEffect(() => {
        if (agmGroupStatus && agmGroupStatus.statusStatement) {
            setVoteInfo(agmGroupStatus.statusStatement)
        }
    }, [agmGroupStatus]);

    const getBio = () => {
        axios.post('/vote/bio', {
            id: voter.uniqueId,
            key: voter.guid,
            ballotId: ballot.id,
            previewId,
            personalId,
            receipt,
            optionId: parseInt(bioId)
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    setBioBallotOption(response.data.payload)
                    setShowBio(true)
                    setLoading(false)
                }
                else {
                    setVoteError(response.data.errorMessage)
                }
            })
            .catch(function (error) {
                alert(error)
            })
    }

    useEffect(() => {
        // Refresh vote component
        if (validateState) {
            refresh()
        }
    }, [validateState]);

    const refresh = (isSetLoading = true) => {
        setLoading(isSetLoading)

        // Clear banners
        setVoteError("")
        setVoteInfo("")
        setJustSubmitted(false)

        // Show bio (either ballot is open or its a preview)?
        if (bioId && (validateState === VoterValidateResult.BallotOpen || isPreview)) {
            getBio();
            return
        }
        // Info page
        else if (isInfoPageUrl) {
            const isShowInfoPage = validateState !== VoterValidateResult.BallotNotPublished
                && validateState !== VoterValidateResult.BallotClosed
            setShowInfoPage(isShowInfoPage)

            // Process status message for info page
            processInfoPageStatus()

            if (isShowInfoPage) {
                getInfoPage()
            }

            setLoading(false)
        }
        // Proxy nomination
        else if (validateState === VoterValidateResult.ProxyOpen || validateState === VoterValidateResult.ProxySubmitted) {
            getBallotProxy()
        }
        else if (ballot.isNominationBallot) {
            getNominationConfig()
        }
        else {
            // Show voting page details?
            const isShowVoting = validateState === VoterValidateResult.BallotOpen
                || validateState === VoterValidateResult.VoteSubmitted
                || validateState === VoterValidateResult.BallotAgmOpen
                || isAdmin
            setShowVoting(isShowVoting)

            // AGMs have their own status messages that supercede standard ballot closed etc
            if (ballot.isAgm && validateState !== VoterValidateResult.BallotOpen) {
                getAgmGroupStatus(() => {
                    if (isShowVoting) {
                        getGroup();
                    }
                    else {
                        setLoading(false)
                    }
                })
            }
            else {
                // Process status message for voting
                processStatus()

                // Only get group if showing vote
                if (isShowVoting) {
                    getGroup();
                }

                setLoading(false)
            }
        }
    }

    const processStatus = () => {
        // Set not published
        if (validateState === VoterValidateResult.BallotNotPublished) {
            setVoteError("Ballot is not available for voting at this time. Please consult your Ballot manager.")
        }
        // Set pause message
        else if (validateState === VoterValidateResult.BallotPaused) {
            setVoteInfo(ballot.pauseMessage ? ballot.pauseMessage : "Ballot is paused")
        }
        // Set ballot closed
        else if (validateState === VoterValidateResult.BallotClosed) {
            setVoteError("Ballot is closed")
        }
        // Set ballot not open yet
        else if (validateState === VoterValidateResult.BallotNotOpenYet) {
            setNotOpen(true)
        }
        else if (validateState === VoterValidateResult.BallotNotAttending) {
            setVoteError("Your attendance must be recorded before you can vote")
        }
    }

    const processInfoPageStatus = () => {
        // Set not published
        if (validateState === VoterValidateResult.BallotNotPublished) {
            setVoteError("Ballot is not available for viewing at this time. Please consult your Ballot manager.")
        }
        // Set ballot closed
        else if (validateState === VoterValidateResult.BallotClosed) {
            setVoteError("Ballot is closed")
        }
    }

    const handleVoteError = (voteError) => {
        setVoteError(voteError)
    }

    const handleOptionsUpdate = (options) => {
        const updatedCurrentGroup = { ...currentGroup }
        updatedCurrentGroup.options = options
        setCurrentGroup(updatedCurrentGroup)
    }

    const handleSubmitVotes = (votes, isVotesSubmitted, onSubmitSuccess, onSubmitFailure) => {
        axios.post('/vote/submit', {
            id: voter.uniqueId,
            key: voter.guid,
            ballotId: ballot.id,
            previewId,
            receipt,
            personalId,
            groupId: currentGroup.groupId,
            votes: votes,
            isSubmitted: isVotesSubmitted
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    setVoteError(null)
                    setReceipt(response.data.payload.receipt)

                    // Set submitted
                    if (isVotesSubmitted) {
                        // Get group again to refresh state (ensure receipt from payload used as the state set above hasnt propogated yet)
                        getGroup(null, null, response.data.payload.receipt, onSubmitSuccess)

                        setJustSubmitted(true)
                    }
                    else if (onSubmitSuccess) {
                        onSubmitSuccess(votes)
                    }
                }
                else {
                    setVoteError(response.data.errorMessage)
                    // Check for invalid option IDs to flag
                    if (response.data.payload) {
                        const invalidOptions = response.data.payload.invalidOptionIds
                        const updatedCurrentGroup = { ...currentGroup }

                        for (let i = 0; i < updatedCurrentGroup.options.length; i++) {
                            updatedCurrentGroup.options[i].isError = invalidOptions.includes(updatedCurrentGroup.options[i].ballotOptionId)
                        }

                        setCurrentGroup(updatedCurrentGroup)
                    }

                    // Submit success
                    if (onSubmitFailure) {
                        onSubmitFailure(votes)
                    }
                }
            })
            .catch(function (error) {
                alert(error)
            })
    }

    const handleSubmit = (isVotesSubmitted, onSubmitSuccess, onSubmitFailure) => {
        // Get votes
        let votes = getVotes(isVotesSubmitted)

        // Submit votes
        handleSubmitVotes(votes, isVotesSubmitted, onSubmitSuccess, onSubmitFailure)
    }

    const handleAgmStatusUpdate = (oldStatus, newStatus) => {
        // Check for status changes where we don't want to show loading
        let isSetLoading = true
        if ((oldStatus === "AgmGroupOpen" && newStatus === "AgmGroupPaused") ||
            (oldStatus === "AgmGroupPaused" && newStatus === "AgmGroupOpen")) {
            isSetLoading = false
        }

        setLoading(isSetLoading)

        if (isSetLoading) {
            // Update voter auth with trigger refresh (and getAgmGroupStatus)
            onUpdateAuth()
        }
        else {
            // Only refresh AGM status
            getAgmGroupStatus()
        }
    }

    const getVotes = (isVotesSubmitted = true) => {
        var votes = []

        currentGroup.options.filter(o => isIncludeOptionForVote(o, currentGroup)).forEach((option) => {
            var vote = {
                ballotOptionId: option.ballotOptionId,
                ballotOptionName: option.ballotOptionName,
                groupId: currentGroup.groupId,
                score: getScoreForVote(option, currentGroup),
                textValue: option.textValue,
                isSubmitted: isVotesSubmitted,
                ibpsaGroup: option.ibpsaGroup
            }
            votes.push(vote)
        })

        return votes
    }

    const isIncludeOptionForVote = (option, group) => {
        // Hidden option? (e.g. Survey's with linked questions)
        if (option.isOptionHidden === 'true') {
            return false
        }

        // IBPSA includes the no votes (but exclude the proxy options as the standard check at bottom will work)
        if (group.groupType === "IBPSA" && option.ballotOptionName !== "Attend" && option.ballotOptionName !== "Proxy") {
            return true
        }

        // Questionnaires includes the no votes
        if (group.groupType === "Questionnaire" && option.score != null) {
            return true
        }

        // Survey text question?
        if (option.textValue && option.textValue !== '') {
            return true
        }

        // Default to only include selected or values greater than 0
        return option.score > 0
    }

    const getScoreForVote = (option) => {
        // If we got this far through the option filter then it implies we want to register a 0 or more for each option
        return option.score ?? 0
    }

    let weightingLabel = null
    const voteWeight = voter.voteWeight
    if (voteWeight > 1 && currentGroupVote && !currentGroupVote.hasVoted) {
        // Update weighting label
        weightingLabel = ballot.isSplitVoting ?
            `Your voter account is authorised to vote up to ${voteWeight} times (${voteWeight - currentGroupVote.numberOfVotes} votes left)` :
            `Your voter account has a vote weighting of ${voteWeight}`
    }

    // Without vote confirmation the server withholds submitted votes, so don't render an empty ballot
    const isShowVoteGroup = !(currentGroupVote?.hasVoted && !ballot.isVoteChange && !ballot.isVoteConfirmation && !isAdmin)

    return (
        <div>
            <VoteHeader
                countdownDateUtc={agmGroupStatus?.openGroup?.closeTime}
                isPaused={agmGroupStatus?.openGroup?.pauseTime != null}
                isShowBorder={true}
            />
            {voteError && <ErrorBanner isSticky="true">{voteError}</ErrorBanner>}
            {voteInfo && <InfoBanner isSticky="true" isSetHtml="true">{voteInfo}</InfoBanner>}
            {isNotOpen && !isAdmin &&
                <>
                    <InfoBanner isSetHtml="true">{ballot.ballotNotOpenStatement}</InfoBanner>
                    <div className="my-8">
                        <Countdown targetDateUtc={ballot.openDateUtc} isDoomsdayFormat={true} />
                    </div>
                </>
            }

            {ballot.isAgm &&
                <AgmStatus
                    onAgmStatusUpdate={handleAgmStatusUpdate}
                    authQueryParams={`vid=${voter.uniqueId}&key=${voter.guid}`} />
            }

            <LoadingPanel className="py-8 px-10 text-center"
                isLoading={isLoading} />

            {isShowBio && !isLoading &&
                <VoteBiography
                    bioBallotOption={bioBallotOption} />}
            {isShowInfoPage && !isLoading && infoPageContent &&
                <VoteInfo
                    isInfoPageEnabled={isInfoPageEnabled}
                    infoPageContent={infoPageContent} />}
            {isShowProxy && ballot && voter && proxyInfo && !isLoading &&
                <div>
                    <ProxyNomination />
                </div>
            }
            {isShowNominations && nominationConfig &&
                <Nominations config={nominationConfig} />
            }
            {isShowVoting && !isLoading &&
                <div>
                    <div className="mb-4">
                        <h2>{voter.name}</h2>
                    </div>
                    {(isJustSubmitted || (currentGroupVote && currentGroupVote.hasVoted)) &&
                        <VoteSubmitted
                            voter={voter}
                            ballot={ballot}
                            groupVote={currentGroupVote}
                            onVoteError={handleVoteError}
                            isJustSubmitted={isJustSubmitted}
                            submitReceipt={receipt}
                        />}
                    {weightingLabel &&
                        <InfoBanner>{weightingLabel}</InfoBanner>
                    }
                    {isShowVoteGroup && (votingType === "SingleGroup" ||
                        votingType === "Agm") &&
                        <SingleGroupVote
                            getGroup={getGroup}
                            currentGroup={currentGroup}
                            currentGroupVote={currentGroupVote}
                            handleSubmit={handleSubmit}
                            isPreview={isPreview}
                            handleOptionsUpdate={handleOptionsUpdate}
                            isJustSubmitted={isJustSubmitted}
                            receipt={receipt}
                            personalId={personalId} />
                    }
                    {isShowVoteGroup && votingType === "MultiGroup" &&
                        <MultiGroupVote
                            receipt={receipt}
                            personalId={personalId}
                            getGroup={getGroup}
                            currentGroup={currentGroup}
                            currentGroupVote={currentGroupVote}
                            groupIds={groupIds}
                            handleSubmit={handleSubmit}
                            handleSubmitVotes={handleSubmitVotes}
                            isPreview={isPreview}
                            previewId={previewId}
                            handleOptionsUpdate={handleOptionsUpdate}
                            handleVoteError={handleVoteError}
                            isJustSubmitted={isJustSubmitted}
                            setJustSubmitted={setJustSubmitted}
                        />
                    }
                </div>}
        </div>
    );
}